import { VotingInterface } from '@/components/VotingInterface';
import { LoadProjectDto } from '@/contracts/models/LoadProjectDto';
import { useImageUrl } from '@/hooks/useImageUrl';
import { useTranslation } from '@/hooks/useTranslation';
import { SurveySummaryDto } from '@/services/feedbackApi';
import { getProject } from '@/services/projectApi';
import { sanitizeRichTextHtml } from '@/utils/htmlSanitizeUtility';
import { format } from 'date-fns';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './ProjectLandingPage.css';

/**
 * Component to display profile image with S3 temp key support
 */
const ProfileImageDisplay: React.FC<{
  profileImage: string;
  profileImageAltText?: string | null;
}> = ({ profileImage, profileImageAltText }) => {
  const { imageUrl, isLoading } = useImageUrl(profileImage);

  if (isLoading) {
    return (
      <div className='absolute left-4 z-50 -top-20 w-40 h-40 rounded-[10px] border-4 border-white shadow-lg bg-gray-200 animate-pulse' />
    );
  }

  if (!imageUrl) {
    return null;
  }

  return (
    <div className='absolute left-4 z-50 -top-20'>
      <img
        src={imageUrl}
        alt={profileImageAltText ?? undefined}
        className='w-40 h-40 rounded-[10px] border-4 border-white shadow-lg object-cover'
      />
    </div>
  );
};

export default function ProjectLandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<LoadProjectDto | null>(null);
  const [surveys, setSurveys] = useState<SurveySummaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadProject = async () => {
      if (!projectId) {
        setError(t('projectLanding.errors.noProjectId'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const projectResponse = await getProject(projectId);
        const projectData = projectResponse.project!;
        setProject(projectData);
        setSurveys(projectResponse.surveys);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : t('projectLanding.errors.loadFailed')
        );
      } finally {
        setLoading(false);
      }
    };

    void loadProject();
  }, [projectId, t]);

  if (loading) {
    return (
      <div
        className='flex justify-center items-center min-h-screen'
        role='status'
        aria-live='polite'
      >
        <ProgressSpinner aria-label={t('common.loadingProject')} />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div
        className='flex flex-col justify-center items-center min-h-screen'
        role='alert'
      >
        <h1 className='text-4xl font-bold mb-4'>
          {t('errors.pageNotFoundTitle')}
        </h1>
        <p className='text-xl text-gray-600'>
          {error || t('projects.errors.notFound')}
        </p>
      </div>
    );
  }

  return (
    <main className='project-landing-page bg-gray-50'>
      {/* Header Banner */}
      {project.headerImage && (
        <div className='project-banner relative w-full'>
          <img
            src={project.headerImage}
            alt={project.headerImageAltText || project.name}
            className='w-full h-64 md:h-96 object-cover'
          />
        </div>
      )}

      {/* Apply additional padding to account for profile image */}
      <div
        className={`max-w-7xl mx-auto px-4 relative z-0 bg-gray-50 ${
          project.profileImage ? 'pt-24 pb-8' : 'py-8'
        }`}
      >
        {/* Profile Image - positioned to align with content */}
        {project.profileImage && (
          <ProfileImageDisplay
            profileImage={project.profileImage}
            profileImageAltText={project.profileImageAltText}
          />
        )}
        {/* Project Title */}
        <div className='mb-8'>
          <h1 className='text-4xl md:text-5xl font-bold text-gray-900'>
            {project.name}
          </h1>
        </div>

        {/* Use grid layout only if there are sidebar items (resource links or text block) */}
        <div
          className={
            (project.resourceLinks && project.resourceLinks.length > 0) ||
            project.textBlockTitle ||
            project.textBlockContent
              ? 'grid grid-cols-1 lg:grid-cols-3 gap-8'
              : ''
          }
        >
          {/* Main Content */}
          <div
            className={
              (project.resourceLinks && project.resourceLinks.length > 0) ||
              project.textBlockTitle ||
              project.textBlockContent
                ? 'lg:col-span-2 space-y-8'
                : 'space-y-8'
            }
          >
            {/* About Our Project Section */}
            <div className='bg-white rounded-lg shadow-md p-6 border border-gray-200'>
              <section className='about-section'>
                <h2 className='text-2xl font-bold text-gray-900 mb-4'>
                  {t('projectLanding.about.title')}
                </h2>
                <div
                  className='rich-text-content max-w-none text-gray-700'
                  dangerouslySetInnerHTML={{
                    __html: sanitizeRichTextHtml(
                      project.projectDescription ?? ''
                    ),
                  }}
                />
              </section>
            </div>

            {/* Available Surveys Section */}
            {surveys.length > 0 && (
              <section className='surveys-section'>
                <h2 className='text-2xl font-bold text-gray-900 mb-6'>
                  {t('projectLanding.surveys.title')}
                </h2>
                <div className='space-y-4'>
                  {surveys.map(survey => (
                    <div
                      key={survey.surveyId}
                      className='survey-card bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow'
                    >
                      <div className='flex flex-row items-center gap-6 p-6'>
                        {/* Left: Content */}
                        <div className='flex-1 min-w-0'>
                          <h3 className='text-xl font-semibold text-gray-900 mb-2'>
                            {survey.title}
                          </h3>

                          <p className='text-gray-600 mb-3 line-clamp-2'>
                            {survey.description}
                          </p>

                          {survey.status === 'completed' ? (
                            <span className='status-badge status-closed'>
                              {t('surveys.status.closed')}
                            </span>
                          ) : (
                            <div className='flex flex-wrap gap-4 text-sm text-gray-500 items-center'>
                              <span className='flex items-center gap-1.5'>
                                <i
                                  className='pi pi-clock text-gray-400'
                                  aria-hidden='true'
                                />
                                {survey.estimatedMinutes}{' '}
                                {t('projectLanding.surveys.minutes')}
                              </span>
                              <span className='flex items-center gap-1.5'>
                                <i
                                  className='pi pi-users text-gray-400'
                                  aria-hidden='true'
                                />
                                {survey.responseCount}{' '}
                                {t('projectLanding.surveys.responses')}
                              </span>
                              <span className='flex items-center gap-1.5'>
                                <i
                                  className='pi pi-user text-gray-400'
                                  aria-hidden='true'
                                />
                                {survey.requireLogin
                                  ? t('projectLanding.surveys.requiresLogin')
                                  : t('projectLanding.surveys.anonymous')}
                              </span>
                              {survey.endDate && (
                                <span className='flex items-center gap-1.5'>
                                  <i
                                    className='pi pi-calendar text-gray-400'
                                    aria-hidden='true'
                                  />
                                  {t('projectLanding.surveys.closes')}{' '}
                                  {format(
                                    new Date(survey.endDate),
                                    'MM/dd/yyyy'
                                  )}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Right: Action buttons */}
                        <div className='flex-shrink-0 flex flex-col gap-2'>
                          {survey.status === 'published' && (
                            <Button
                              label={t('projectLanding.surveys.startSurvey')}
                              icon='pi pi-check-circle'
                              onClick={() =>
                                navigate(`/survey/${survey.surveyId}`)
                              }
                              aria-label={t(
                                'projectLanding.surveys.startSurveyAriaLabel',
                                {
                                  surveyTitle: survey.title,
                                }
                              )}
                            />
                          )}
                          {survey.mixerQuestionId && (
                            <Button
                              label={t('projectLanding.surveys.viewMixer')}
                              icon='pi pi-comments'
                              outlined={true}
                              onClick={() => {
                                navigate(`/mixer/${survey.surveyId}`);
                              }}
                              aria-label={t(
                                'projectLanding.surveys.viewMixerAriaLabel'
                              )}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Community Mixer Section */}
            {project.displayMixerQuestion && project.mixerSurveyId && (
              <section className='community-mixer-section'>
                <h2 className='text-2xl font-bold text-gray-900 mb-4'>
                  {t('projectLanding.communityMixer.title')}
                </h2>
                <p className='text-gray-600 mb-6 text-xl'>
                  {t('projectLanding.communityMixer.description')}
                </p>
                <div className='bg-white rounded-lg shadow-md p-6 border border-gray-200'>
                  <VotingInterface
                    surveyId={project.mixerSurveyId}
                    responseId=''
                    showHeader={false}
                  />
                </div>
              </section>
            )}
          </div>

          {/* Sidebar - rendered when there are sidebar items (resource links or text block) */}
          {((project.resourceLinks && project.resourceLinks.length > 0) ||
            project.textBlockTitle ||
            project.textBlockContent) && (
            <div className='lg:col-span-1 space-y-6'>
              {/* Text Block Card - only rendered when there is content */}
              {(project.textBlockTitle || project.textBlockContent) && (
                <div className='bg-white rounded-lg shadow-md p-6 border border-gray-200'>
                  {project.textBlockTitle && (
                    <h3 className='font-semibold text-gray-900 mb-3'>
                      {project.textBlockTitle}
                    </h3>
                  )}
                  {project.textBlockContent && (
                    <div
                      className='rich-text-content max-w-none text-gray-700'
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichTextHtml(project.textBlockContent),
                      }}
                    />
                  )}
                </div>
              )}

              {/* Resource Links Card */}
              {project.resourceLinks && project.resourceLinks.length > 0 && (
                <div className='bg-white rounded-lg shadow-md p-6 border border-gray-200'>
                  <h3 className='font-semibold text-gray-900 mb-3'>
                    {t('projectLanding.sidebar.resourceLinks')}
                  </h3>
                  <ul className='space-y-2'>
                    {project.resourceLinks.map((link, index) => (
                      <li key={index}>
                        <a
                          href={link.url}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-blue-600 hover:text-blue-800 underline'
                        >
                          {link.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
